import type { SimpleStorage } from '@okta/okta-auth-js';
import type { CookieOptions } from './cookie-chunker';
import { getChunked, removeChunked, setChunked } from './cookie-chunker';

/**
 * Implements Okta's `SimpleStorage` contract on top of cookie-chunker so the
 * Okta token bundle survives the browser's ~4KB per-cookie size limit.
 *
 * Wire into the OktaAuth constructor via `storageManager.token.storageProvider`
 * (see `app.module.ts`). The Okta SDK calls this synchronously, so we cannot
 * use the async Cookie Store API and must rely on `document.cookie`.
 */
export class CookieStorageProvider implements SimpleStorage {
  private readonly options: CookieOptions;

  constructor(options: CookieOptions = {}) {
    this.options = options;
  }

  getItem(key: string): string {
    try {
      const value = getChunked(key);
      if (!value) return '';

      // Okta expects this storage value to always be a serialized JSON object.
      if (!this.isValidJson(value)) {
        removeChunked(key, this.options);
        return '';
      }

      return value;
    } catch {
      removeChunked(key, this.options);
      return '';
    }
  }

  setItem(key: string, value: string): void {
    setChunked(key, value, this.options);
  }

  removeItem(key: string): void {
    removeChunked(key, this.options);
  }

  private isValidJson(value: string): boolean {
    try {
      const parsed = JSON.parse(value);
      return typeof parsed === 'object' && parsed !== null && !Array.isArray(parsed);
    } catch {
      return false;
    }
  }
}
